import { Drawing } from "./drawing";
import { Union, Intersection, Difference } from "../base/operations";
import { Chain } from "../base/chain";
import { Matrix } from "../base/matrix";
import { Pt } from "../base/pt";
import { Element } from "./svg";
import { group } from "./group";

class Shape extends Drawing {
  closedChains: Chain[];

  constructor(
    closedChains: Chain[],
    color: string,
    labels: Map<string, Pt>,
    anchorPt?: Pt
  ) {
    super(color, labels, anchorPt);
    this.closedChains = closedChains;
  }

  label(name: string, pt: Pt): Shape {
    const newLabels = new Map(this.labels);
    newLabels.set(name, pt);
    return new Shape(
      this.closedChains,
      this.color,
      newLabels,
      this.anchorPt
    );
  }

  anchorAt(pt: Pt): Shape {
    return new Shape(
      this.closedChains,
      this.color,
      this.labels,
      pt
    )
  }

  stroke(color: string): Shape {
    return new Shape(
      this.closedChains,
      color,
      this.labels,
      this.anchorPt
    );
  }

  transform(matrix: Matrix): Shape {
    return new Shape(
      this.closedChains.map((c) => c.transform(matrix)),
      this.color,
      this.transformLabels(matrix),
      this.transformAnchor(matrix)
    );
  }

  get elements(): Element[] {
    return this.closedChains.map((c) => {
      let el = { chain: c, stroke: this.color, fill: this.color, opacity: 0.1 };
      return el;
    });
  }

  get points(): Pt[] {
    return this.closedChains.flatMap((c) => c.segments.map((s) => s.end));
  }

  get drawings() {
    return this.closedChains.map(
      (c) =>
        new Shape(
          [c],
          this.color,
          this.labels
        )
    );
  }

  get chains() {
    return this.closedChains;
  }
}

function key(pt: Pt) {
  return pt.x + ":" + pt.y;
}

class ChainDrawing extends Drawing {
  chain: Chain;

  constructor(chain: Chain, color: string) {
    super(color, new Map());
    this.chain = chain;
  }

  stroke(color: string): ChainDrawing {
    return new ChainDrawing(this.chain, this.color)
  }

  label(name: string, pt: Pt): ChainDrawing {
    return this;
  }

  transform(matrix: Matrix): ChainDrawing {
    return new ChainDrawing(this.chain.transform(matrix), this.color);
  }

  anchorAt(pt: Pt): ChainDrawing {
    return this;
  }

  get drawings() {
    return [this];
  }

  get points() {
    const pts = this.chain.segments.map((s) => s.end);
    pts.unshift(this.chain.segments[0].start);
    return pts;
  }

  get chains() {
    return [this.chain];
  }

  get elements() {
    return [
      { chain: this.chain, stroke: this.color, fill: "none", opacity: 1.0 },
    ];
  }
}

function operation(op, shape1, shape2, debug): Drawing {
  const o = new op(shape1.closedChains, shape2.closedChains);
  if (debug) {
    const keep = o.keep.map((c) => new ChainDrawing(c, "green"));
    const drop = o.drop.map((c) => new ChainDrawing(c, "red"));
    return group(keep.concat(drop));
  } else {
    let newChains = o.chains;
    let allPts = new Set();
    newChains.forEach((c) => c.segments.forEach((s) => allPts.add(key(s.end))));
    let newLabels = new Map();
    for (let pair of shape2.labels) {
      if (allPts.has(key(pair[1]))) newLabels.set(pair[0], pair[1]);
    }
    for (let pair of shape1.labels) {
      if (allPts.has(key(pair[1]))) newLabels.set(pair[0], pair[1]);
    }
    return new Shape(
      newChains,
      shape1.color,
      newLabels
    );
  }
}

function union(shape1: Shape, shape2: Shape, debug: boolean = false): Drawing {
  return operation(Union, shape1, shape2, debug);
}

function intersection(
  shape1: Shape,
  shape2: Shape,
  debug: boolean = false
): Drawing {
  return operation(Intersection, shape1, shape2, debug);
}

function difference(
  shape1: Shape,
  shape2: Shape,
  debug: boolean = false
): Drawing {
  return operation(Difference, shape1, shape2, debug);
}

export { Shape, union, intersection, difference };
